(() => {
  const OPEN_CLASS = 'is-open';
  const ACTIVE_CLASS = 'is-active';
  const HAS_ACTIVE_CLASS = 'has-active';
  const NAVIGATING_CLASS = 'category-menu-is-navigating';

  const CATEGORY_DETAILS_SELECTOR = '.menu-drawer__category[open]';
  const CATEGORY_NESTED_DETAILS_SELECTOR = '.menu-drawer__category details[open]';
  const HEADER_DRAWER_SELECTOR = '.menu-drawer-container[open]';
  const HEADER_DRAWER_NESTED_DETAILS_SELECTOR = '.menu-drawer-container details[open]';

  const SEARCH_TARGETS = [
    '.header__icon--search',
    '.header__search',
    'search-modal',
    'details-modal',
    'input[type="search"]',
    '.search__input',
    '.field__input'
  ].join(',');

  const LOCALIZATION_TARGETS = [
    '.header__localization-button',
    '.header__localization-lang-button',
    '.localization-form__select',
    '.disclosure__button'
  ].join(',');

  const ACCOUNT_TARGETS = [
    '.header__icon--account',
    'a[href*="/account"]'
  ].join(',');

  const CART_TARGETS = [
    '.header__icon--cart',
    'cart-drawer-opener',
    '[data-cart-drawer-open]',
    'a[href="/cart"]',
    'a[href$="/cart"]'
  ].join(',');

  const setNavigatingState = () => {
    document.documentElement.classList.add(NAVIGATING_CLASS);
  };

  const clearNavigatingState = () => {
    document.documentElement.classList.remove(NAVIGATING_CLASS);
  };

  const closeCategoryDrawerMenu = () => {
    document
      .querySelectorAll(`${CATEGORY_DETAILS_SELECTOR}, ${CATEGORY_NESTED_DETAILS_SELECTOR}`)
      .forEach((details) => {
        details.removeAttribute('open');
      });
  };

  const closeHeaderDrawerMenu = () => {
    document
      .querySelectorAll(`${HEADER_DRAWER_NESTED_DETAILS_SELECTOR}, ${HEADER_DRAWER_SELECTOR}`)
      .forEach((details) => {
        details.removeAttribute('open');
      });
  };

  const closeDesktopCategoryMegaMenus = () => {
    document.querySelectorAll('.list-menu--category-mega.is-open').forEach((menuItem) => {
      menuItem.classList.remove(OPEN_CLASS);

      const tabs = menuItem.querySelector('.rpg-category-mega__tabs');
      const tabItems = Array.from(
        menuItem.querySelectorAll('.rpg-category-mega__tab-item')
      );

      tabItems.forEach((item) => {
        item.classList.remove(ACTIVE_CLASS);
      });

      if (tabs) {
        tabs.classList.remove(HAS_ACTIVE_CLASS);
      }
    });
  };

  const closeAllCategoryMenus = () => {
    closeCategoryDrawerMenu();
    closeHeaderDrawerMenu();
    closeDesktopCategoryMegaMenus();
  };

  const blurCurrentElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const isSamePageAnchor = (link) => {
    if (!link || !link.href) return false;

    const linkUrl = new URL(link.href, window.location.origin);

    return (
      linkUrl.origin === window.location.origin &&
      linkUrl.pathname === window.location.pathname &&
      linkUrl.search === window.location.search &&
      linkUrl.hash
    );
  };

  const isRealNavigationLink = (link) => {
    if (!link || !link.href) return false;

    const href = link.getAttribute('href');

    if (!href) return false;
    if (href.startsWith('#')) return false;
    if (href.startsWith('javascript:')) return false;
    if (isSamePageAnchor(link)) return false;

    return true;
  };

  const initCategoryMegaMenus = () => {
    document.querySelectorAll('.list-menu--category-mega').forEach((menuItem) => {
      if (menuItem.dataset.categoryMegaInitialized === 'true') return;

      const trigger = menuItem.querySelector('.header__menu-item');
      const megaMenu = menuItem.querySelector('.rpg-category-mega');
      const tabs = megaMenu?.querySelector('.rpg-category-mega__tabs');
      const tabItems = Array.from(
        megaMenu?.querySelectorAll('.rpg-category-mega__tab-item') || []
      );

      if (!trigger || !megaMenu || !tabs || tabItems.length === 0) return;

      let closeTimer = null;

      const activateTab = (activeItem) => {
        tabItems.forEach((item) => {
          item.classList.toggle(ACTIVE_CLASS, item === activeItem);
        });

        tabs.classList.add(HAS_ACTIVE_CLASS);
      };

      const resetTabs = () => {
        tabItems.forEach((item) => {
          item.classList.remove(ACTIVE_CLASS);
        });

        tabs.classList.remove(HAS_ACTIVE_CLASS);

        if (tabItems[0]) {
          tabItems[0].classList.add(ACTIVE_CLASS);
          tabs.classList.add(HAS_ACTIVE_CLASS);
        }
      };

      const openMenu = () => {
        clearNavigatingState();
        window.clearTimeout(closeTimer);
        menuItem.classList.add(OPEN_CLASS);

        const hasActiveTab = tabItems.some((item) =>
          item.classList.contains(ACTIVE_CLASS)
        );

        if (!hasActiveTab) {
          resetTabs();
        }
      };

      const closeMenu = () => {
        closeTimer = window.setTimeout(() => {
          menuItem.classList.remove(OPEN_CLASS);
          resetTabs();
        }, 80);
      };

      trigger.addEventListener('mouseenter', openMenu);
      trigger.addEventListener('focus', openMenu);

      menuItem.addEventListener('mouseleave', closeMenu);

      megaMenu.addEventListener('mouseenter', () => {
        window.clearTimeout(closeTimer);
      });

      megaMenu.addEventListener('mouseleave', closeMenu);

      tabItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          activateTab(item);
        });
      });

      megaMenu.addEventListener('click', (event) => {
        const link = event.target.closest('a');

        if (!link) return;

        if (isRealNavigationLink(link)) {
          setNavigatingState();
        }

        link.blur();
        blurCurrentElement();

        menuItem.classList.remove(OPEN_CLASS);
      });

      resetTabs();

      menuItem.dataset.categoryMegaInitialized = 'true';
    });
  };

  const initCategoryDrawerMenuCloseBehavior = () => {
    if (document.documentElement.dataset.categoryDrawerCloseInitialized === 'true') return;

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        const clickedSearchTarget = target.closest(SEARCH_TARGETS);
        const clickedLocalizationTarget = target.closest(LOCALIZATION_TARGETS);
        const clickedAccountTarget = target.closest(ACCOUNT_TARGETS);
        const clickedCartTarget = target.closest(CART_TARGETS);

        const clickedAnotherTopMenu = target.closest(
          '.menu-drawer__menu > li > details > summary'
        );

        const clickedCategorySummary = target.closest(
          '.menu-drawer__category > summary'
        );

        if (clickedSearchTarget) {
          closeCategoryDrawerMenu();
          closeDesktopCategoryMegaMenus();
          return;
        }

        if (clickedLocalizationTarget) {
          closeCategoryDrawerMenu();
          closeDesktopCategoryMegaMenus();
          return;
        }

        if (clickedAccountTarget) {
          closeCategoryDrawerMenu();
          closeDesktopCategoryMegaMenus();
          return;
        }

        if (clickedCartTarget) {
          closeAllCategoryMenus();
          blurCurrentElement();
          return;
        }

        if (clickedAnotherTopMenu && !clickedCategorySummary) {
          closeCategoryDrawerMenu();
        }
      },
      true
    );

    document.addEventListener('click', (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const clickedInsideCategory = target.closest('.menu-drawer__category');
      const clickedLinkInsideCategory = target.closest('.menu-drawer__category a');

      if (clickedLinkInsideCategory) {
        if (isRealNavigationLink(clickedLinkInsideCategory)) {
          setNavigatingState();
        }

        closeAllCategoryMenus();
        blurCurrentElement();
        return;
      }

      if (!clickedInsideCategory) {
        closeCategoryDrawerMenu();
      }
    });

    window.addEventListener('pagehide', () => {
      setNavigatingState();
      closeAllCategoryMenus();
    });

    window.addEventListener('pageshow', () => {
      clearNavigatingState();
      closeAllCategoryMenus();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        setNavigatingState();
        closeAllCategoryMenus();
      }
    });

    document.documentElement.dataset.categoryDrawerCloseInitialized = 'true';
  };

  const init = () => {
    initCategoryMegaMenus();
    initCategoryDrawerMenuCloseBehavior();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();